import React from "react";
import { useNavigate } from "react-router";
import HttpCouponsRepository from "../../api/request/Coupons/Coupons.service";
import HttpLeadRepository from "../../api/request/Lead/Lead.service";
import { GetlifeContext } from "../../context/GetLifeContext.context";
import { I18nContext } from "../../context/i18n.context";
import { calculateDiffDays, formatDate } from "../../utils/Dates.utils";
import numberFormat, { capitalize, numberFormatPayment } from "../../utils/Utils";
import { redirect } from "../../Routes/redirect";
import { DOMAIN } from "../../constants/Global";
import { renderCoveragesWithCapitals } from "../../utils/dataBlockCoverages";
import { CapitalsResponse } from "../../api/request/Lead/Interfaces/DataInterface.response";
import { TraitName } from "../../api/request/Lead/Interfaces/TraitTypes";
import { isNativeMode } from "../../native/nativeMode";
import { readOrganicOffer } from "../../native/organicOffer";

export type DataBlockIconT = 'card' | 'user' | 'check' | 'umbrella' | 'calendar'

interface DataBlockInfoI {
    title: string,
    subtitle: string,
    text: string | React.ReactNode,
    icon: DataBlockIconT,
    space: boolean
}

const SQL_DOMAINS = ["sql_es"];

const getActiveTraits = (capitals?: CapitalsResponse): TraitName[] =>
  capitals
    ? (Object.keys(capitals) as TraitName[]).filter(
        (key) => capitals[key]?.active
      )
    : [];

const getDeathCapital = (
  capitals: CapitalsResponse | undefined,
  fallback?: number
): number => capitals?.DEATH?.capital ?? fallback ?? 0;

const coverageSubtitle = (
  capitals: CapitalsResponse | undefined,
  translate: (key: string) => string
): string => {
  const extras = getActiveTraits(capitals).filter((trait) => trait !== "DEATH");
  if (extras.length === 0) {
    return translate("confirmationPage.resume.insurance");
  }
  if (extras.length === 1 && extras[0] === "SERIOUS_ILLNESS") {
    return translate("confirmationPage.resume.eg");
  }
  return translate("confirmationPage.resume.insuranceIPA");
};

const ConfirmationPageController = () => {
  const isSqlDomain = SQL_DOMAINS.includes(DOMAIN ?? "");
  const { leadId, token, handleCallMe, userStatus, setUserStatus, isABtesting } =
    React.useContext(GetlifeContext);
  const {
    state: { translate },
  } = React.useContext(I18nContext);
  const [data, setData] = React.useState<any>();
  const [open, setOpen] = React.useState<boolean>(false);
  const [product, setProduct] = React.useState<any>();
  const [couponInfo, setCouponInfo] = React.useState<any>();
  const [progression, setProgression] = React.useState<any>();
  const [dataBlocksInfo, setDataBlocksInfo] = React.useState<DataBlockInfoI[]>()
  const [capitals, setCapitals] = React.useState<CapitalsResponse | undefined>()

  const leadRepository = new HttpLeadRepository(leadId, token);
  const couponRepository = new HttpCouponsRepository(leadId, token);
  const navigate = useNavigate();

  const intentionMessage = (info?: any) => {
    const source = info || product;
    if (!source?.intention) {
      return "";
    }
    const rawIntention = source.intention;
    const intentionKeys: string[] = (
      Array.isArray(rawIntention) ? rawIntention : String(rawIntention).split(",")
    )
      .map((key: string) => String(key).trim())
      .filter((key: string) => key !== "");
    if (intentionKeys.length === 0) {
      return "";
    }
    const labels = intentionKeys.map((key: string) =>
      translate(`confirmationPage.intention.${key}`)
    );
    const combineIntention =
      labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(", ")} ${translate(
            "confirmationPage.intention.conjuction"
          )} ${labels[labels.length - 1]}`;
    return translate("confirmationPage.intention.protect").replace(
      "$",
      combineIntention
    );
  };

  const continueProcess = () => {
    navigate(redirect(userStatus));
  };
  const fetch = async () => {
    const response = isSqlDomain
      ? await leadRepository.getConfirmationInfo()
      : await leadRepository.getInfoLead();
    setUserStatus(response.status);
    setCapitals(response?.capitals);
    setProgression([
      {
        description: translate(
          "confirmationPage.Progression.confirmMethod.description"
        ),
        title: translate("confirmationPage.Progression.confirmMethod.title"),
        active: isSqlDomain ? "active" : (response.basicInfo && "active"),
      },
      {
        description: translate(
          "confirmationPage.Progression.personalData.description"
        ),
        title: translate("confirmationPage.Progression.personalData.title"),
        active: isSqlDomain ? false : (response.paidAndSigned && "active"),
      },
      {
        description: translate(
          "confirmationPage.Progression.final.description"
        ),
        title: translate("confirmationPage.Progression.final.title"),
        active: isSqlDomain
          ? false
          : (response.basicInfo &&
            response.healthQuestions &&
            response.paidAndSigned &&
            "active"),
      },
    ]);

    if (isSqlDomain) {
      setData(response);
      const isPromoted = response?.promo ? true : false;
      const totalPrice =
        response?.period?.toLowerCase() === "yearly"
          ? !isPromoted
            ? response?.prices?.yearly?.real
            : response?.prices?.yearly?.promoted
          : !isPromoted
          ? response?.prices?.monthly?.real
          : response?.prices?.monthly?.promoted;
      const responseCapitals: CapitalsResponse | undefined = response?.capitals;
      const responseDeathCapital = getDeathCapital(responseCapitals, response?.capital);
      setDataBlocksInfo([
        {
          title: translate("confirmationPage.resume.insured"),
          subtitle: `${capitalize(response.name)}`,
          text: formatDate(response.birthDate),
          icon: "user",
          space: false,
        },
        {
          title: translate("confirmationPage.resume.capital"),
          subtitle: numberFormat(responseDeathCapital, "capital") || "",
          text: intentionMessage(response),
          icon: "check",
          space: true,
        },
        {
          title: translate("confirmationPage.resume.coverage"),
          subtitle: coverageSubtitle(responseCapitals, translate),
          text: renderCoveragesWithCapitals(responseCapitals, translate) ?? "",
          icon: "umbrella",
          space: true,
        },
        {
          title: translate("confirmationPage.resume.startDate"),
          subtitle: new Date(response.effectDate).toLocaleDateString(),
          text: `${calculateDiffDays(response.effectDate, translate)} `,
          icon: "calendar",
          space: true,
        },
        {
          title: translate("confirmationPage.resume.bonus"),
          subtitle: `${numberFormatPayment(totalPrice)} \u20AC`,
          text: translate(
            `confirmationPage.period.${response.period?.toLowerCase()}`
          ),
          icon: "card",
          space: true,
        },
      ]);
    }
  };

  const fetchCoupons = async ({ code, yearlyprice }: any) => {
    try {
      const response = await couponRepository.checkPromotionalCode({
        promotionalCode: code,
        yearlyPrice: yearlyprice,
      });
      if (response.is_applicable_promo) {
        setCouponInfo({
          conditions: response.promo_conditions,
          exceptions: response.promo_exceptions,
          code: response.promo,
        });
      }
    } catch (error) {}
  };

  React.useEffect(() => {
    (async () => {
      if (isSqlDomain) {
        fetch();
      } else if (isNativeMode()) {
        // Native: el `product` del monolito (/checkout/product) no aplica — el lead
        // organic no vive en el monolito, devuelve vacío (precio NaN, fecha Invalid,
        // period undefined). Se arma el `product` desde checkout-data native, que
        // sirve ms-agl: capital/período/fecha de efecto + precio a cobrar. Sin promo
        // (AGL IT). Así el componente y el segundo effect lo consumen sin cambios.
        leadRepository.getCheckoutData().then((d: any) => {
          setData(d);
          const charged = d?.priceSummary?.priceToPay ?? d?.price;
          setProduct({
            capital: d?.lead?.capital,
            period: d?.lead?.purchasePeriod,
            effectDate: d?.effectDate,
            finalPrice: charged,
            finalYearlyPrice: charged,
            promotion: null,
            // Intención elegida en el paso de preguntas (sesión organic) para el
            // mensaje "per proteggere ..."; ms-agl no la persiste.
            intention: readOrganicOffer()?.intention,
          });
        });
        fetch();
      } else {
        leadRepository.getCheckoutData().then(setData);
        leadRepository.getCheckoutProduct().then((prod) => {
          setProduct(prod);
          if (prod.promotion && prod.promotion.promotionalCode) {
            fetchCoupons({
              code: prod.promotion.promotionalCode,
              yearlyprice: prod.finalYearlyPrice,
            });
          }
        });
        fetch();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if(dataBlocksInfo === undefined && data && product && capitals){
      const deathCapital = getDeathCapital(capitals, data.lead?.capital);
      setTimeout(() => {
        setDataBlocksInfo([
          {
            title: translate("confirmationPage.resume.insured"),
            subtitle: `${capitalize(data.lead.name)} ${capitalize(
              data.lead.lastName
            )}`,
            text: formatDate(data.lead.birthdate),
            icon: "user",
            space: false
          },
          {
            title: translate("confirmationPage.resume.capital"),
            subtitle: numberFormat(deathCapital, "capital") || "",
            text: intentionMessage(),
            icon: "check",
            space: true
          },
          {
            title: translate("confirmationPage.resume.coverage"),
            subtitle: coverageSubtitle(capitals, translate),
            text: renderCoveragesWithCapitals(capitals, translate) ?? "",
            icon: "umbrella",
            space: true
          },
          {
            title: translate("confirmationPage.resume.startDate"),
            subtitle: new Date(product.effectDate).toLocaleDateString(),
            text: `${calculateDiffDays(product.effectDate, translate)} `,
            icon: "calendar",
            space: true
          },
          {
            title: translate("confirmationPage.resume.bonus"),
            subtitle: price() || "",
            text: translate(`confirmationPage.period.${product.period?.toLowerCase()}`),
            icon: "card",
            space: true
          },
        ])
      }, isABtesting ? 3000 : 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, product, capitals])

  const price = () => {
    const totalPrice =
      product.period?.toLowerCase() === "yearly"
        ? product.finalYearlyPrice
        : product.finalPrice;
    return `${numberFormatPayment(totalPrice)} €`;
  };



  return {
    continueProcess,
    couponInfo,
    data,
    dataBlocksInfo,
    handleCallMe,
    intentionMessage,
    open,
    price,
    product,
    progression,
    setOpen,
    translate,
    isABtesting,
    isSqlDomain,
    deathCapital: getDeathCapital(capitals),
  };
};

export default ConfirmationPageController;
